import React from 'react';

export interface VariantOption {
  id: string;
  label: string;
}

export interface VariantType {
  id: string;
  name: string;
  options: VariantOption[];
  defaultOptionId?: string;
}

interface VariantEditorProps {
  value: VariantType[];
  onChange: (variants: VariantType[]) => void;
  maxVariantTypes?: number;
}

export function VariantEditor({ value, onChange, maxVariantTypes = 3 }: VariantEditorProps) {
  // Helper to generate unique IDs
  function uid() {
    return Math.random().toString(36).slice(2, 10);
  }

  // Add a new variant type
  function addVariantType() {
    if (value.length >= maxVariantTypes) return;
    onChange([
      ...value,
      {
        id: uid(),
        name: '',
        options: [],
      },
    ]);
  }

  // Remove a variant type
  function removeVariantType(id: string) {
    onChange(value.filter(v => v.id !== id));
  }

  // Update a variant type
  function updateVariantType(id: string, update: Partial<VariantType>) {
    onChange(value.map(v => (v.id === id ? { ...v, ...update } : v)));
  }

  // Add an option to a variant type
  function addOption(variantId: string) {
    onChange(
      value.map(v =>
        v.id === variantId
          ? {
              ...v,
              options: [
                ...v.options,
                { id: uid(), label: '' },
              ],
            }
          : v
      )
    );
  }

  // Remove an option from a variant type
  function removeOption(variantId: string, optionId: string) {
    onChange(
      value.map(v =>
        v.id === variantId
          ? { ...v, options: v.options.filter(o => o.id !== optionId) }
          : v
      )
    );
  }

  // Update an option in a variant type
  function updateOption(variantId: string, optionId: string, update: Partial<VariantOption>) {
    onChange(
      value.map(v =>
        v.id === variantId
          ? {
              ...v,
              options: v.options.map(o =>
                o.id === optionId ? { ...o, ...update } : o
              ),
            }
          : v
      )
    );
  }

  return (
    <div className="space-y-8">
      <div className="mb-2">
        <h3 className="font-bold text-lg text-gray-800 mb-3">Variants</h3>
        <button
          type="button"
          className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-800 font-medium hover:opacity-80 transition-opacity"
          onClick={addVariantType}
          disabled={value.length >= maxVariantTypes}
        >
          <div className="w-6 h-6 rounded-full bg-gray-700 flex items-center justify-center">
            <span className="text-white text-sm font-bold leading-none">+</span>
          </div>
          <span>Add options</span>
        </button>
      </div>
      {value.map((variant, idx) => (
        <div key={variant.id} className="border rounded-lg p-4 space-y-4 bg-neutral-50">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Variant Type <span className="text-black">*</span>
            </label>
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="border rounded px-3 py-2 flex-1"
                placeholder={`Variant type (e.g., Size, Spice Level)`}
                value={variant.name}
                onChange={e => updateVariantType(variant.id, { name: e.target.value })}
                required
              />
              <button
                type="button"
                className="text-red-500 font-bold px-2"
                onClick={() => removeVariantType(variant.id)}
                aria-label="Remove variant type"
              >
                ×
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <div className="font-medium text-gray-700">
              Options <span className="text-black">*</span>
            </div>
            {variant.options.map((option, oidx) => (
              <div key={option.id} className="space-y-1">
                <label className="block text-sm font-medium text-gray-700">
                  Option {oidx + 1} <span className="text-black">*</span>
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    className="border rounded px-2 py-1 flex-1"
                    placeholder="Option label (e.g., Large)"
                    value={option.label}
                    onChange={e => updateOption(variant.id, option.id, { label: e.target.value })}
                    required
                  />
                  <button
                    type="button"
                    className="text-red-500 font-bold px-2"
                    onClick={() => removeOption(variant.id, option.id)}
                    aria-label="Remove option"
                  >
                    ×
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              className="px-3 py-1 rounded text-white font-semibold mt-2"
              style={{
                background: 'linear-gradient(135deg, #f4a261 0%, #e76f51 50%, #f4a261 100%)',
                backgroundSize: '200% 200%',
                animation: 'gradientShift 3s ease infinite'
              }}
              onClick={() => addOption(variant.id)}
              disabled={variant.options.length >= 10}
            >
              + Add Option
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

export default VariantEditor; 